import { supabase } from './supabase'

export const WEAR_FUNNEL_STEPS = Object.freeze([
  'add_opened',
  'search_opened',
  'query_typed',
  'result_selected',
  'confirm_shown',
  'wear_committed',
  'flow_abandoned',
  'flow_blocked',
])

const STEP_VOCABULARY = new Set(WEAR_FUNNEL_STEPS)
const ENTRY_POINTS = new Set([
  'home_hero',
  'bottom_bar_plus',
  'add_button',
  'search_tab',
  'collection_card',
  'bottle_page',
  'memory_anniversary',
  'other',
])
const sessionRuntime = new WeakMap()

function platform() {
  if (typeof navigator === 'undefined') return 'web'
  const value = navigator.userAgent || ''
  if (/android/i.test(value)) return 'android'
  if (/iPad|iPhone|iPod/i.test(value)) return 'ios'
  return 'web'
}

function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  const bytes = globalThis.crypto?.getRandomValues
    ? globalThis.crypto.getRandomValues(new Uint8Array(16))
    : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const value = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('')
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`
}

function runtimeFor(session) {
  if (!sessionRuntime.has(session)) {
    sessionRuntime.set(session, {
      emitted: new Set(),
      retainers: 0,
      terminal: null,
    })
  }
  return sessionRuntime.get(session)
}

function insertWithoutWaiting(row) {
  try {
    // Supabase builders are thenable. Promise.resolve starts the request, while
    // the terminal catch contains PostgREST and network failures. Wear writes
    // never await, receive, or otherwise depend on this telemetry request.
    void Promise.resolve(supabase.from('wear_funnel_events').insert(row))
      .then(({ error }) => {
        if (error) throw error
      })
      .catch(() => undefined)
  } catch {
    // A synchronous client/setup failure is diagnostic exhaust too. Telemetry
    // must not become a new failure mode for logging a wear.
  }
}

export function createWearFunnelSession(entryPoint = 'other', meta = null) {
  try {
    const session = Object.freeze({
      sessionId: uuid(),
      entryPoint: ENTRY_POINTS.has(entryPoint) ? entryPoint : 'other',
    })
    runtimeFor(session)
    emitWearFunnelEvent(session, 'add_opened', { meta })
    return session
  } catch {
    return null
  }
}

export function isWearFunnelSessionActive(session) {
  try {
    return Boolean(session && !runtimeFor(session).terminal)
  } catch {
    return false
  }
}

export function emitWearFunnelEvent(session, step, {
  fragranceId = null,
  blockedReason = null,
  meta = null,
} = {}) {
  try {
    if (!session || !STEP_VOCABULARY.has(step)) return
    const runtime = runtimeFor(session)
    if (step !== 'flow_blocked' && runtime.emitted.has(step)) return
    if (runtime.terminal) return

    runtime.emitted.add(step)
    if (step === 'wear_committed' || step === 'flow_abandoned') {
      runtime.terminal = step
    }

    insertWithoutWaiting({
      session_id: session.sessionId,
      step,
      entry_point: session.entryPoint,
      fragrance_id: fragranceId,
      blocked_reason: blockedReason,
      platform: platform(),
      client_ts: new Date().toISOString(),
      meta,
    })
  } catch {
    // The complete instrumentation surface is best-effort, not only fetch.
  }
}

export function abandonWearFunnelSession(session, meta = null) {
  if (!isWearFunnelSessionActive(session)) return
  emitWearFunnelEvent(session, 'flow_abandoned', { meta })
}

export function retainWearFunnelSession(session) {
  if (!session) return () => {}
  let runtime
  try {
    runtime = runtimeFor(session)
    runtime.retainers += 1
  } catch {
    return () => {}
  }
  let retained = true

  return () => {
    if (!retained) return
    retained = false
    runtime.retainers = Math.max(0, runtime.retainers - 1)

    // React StrictMode mounts, cleans up, and mounts effects again in the same
    // turn. Defer the check so that diagnostic cycle is not a real abandon.
    setTimeout(() => {
      if (runtime.retainers === 0) {
        abandonWearFunnelSession(session, { reason: 'navigation_away' })
      }
    }, 0)
  }
}
